import { cn } from '@/lib/utils';
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next';
import { TruncatedText } from '@/components/common/truncated-text';
import { CopyClipboard } from '@/components/common/copy-clipboard';
import { formatDateToYYYYMMDD } from '@/utils/date-formatter';
import { IconButton } from '@/components/common/icon-button';
import { DeleteIcon, DisableIcon, EditIcon, EnableIcon, MoreIcon } from '@/components/agent-dashboard/assets';
import { MoreActionItem, MoreActionPopover } from '@/components/common/more-action-popover';
import { ConfirmModal } from '@/components/common/confirm-modal';
import { Pagination } from '@/components/common/pagination';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { useUserManagementHooks } from '@/manager';
import { useI18nNavigate } from '@/hooks/use-i18n-navigate';
import { AgentUsersData } from '@/services/agent-v2-users-list';
import { ExpirationVerify } from '@/components/agent-dashboard/expiration-verify';

interface TableListProps {
  users: AgentUsersData[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  changePage: (page: number) => void;
  search: () => void;
}

/** 用户列表 */
export const TableList = ({
  users,
  loading,
  total,
  page,
  pageSize,
  changePage,
  search,
}: TableListProps) => {
  const { t } = useTranslation();
  const navigate = useI18nNavigate();

  const { loading: toggleLoading, toggleUserStatus } = useUserManagementHooks.useToggleUserStatus();

  const { loading: deleteLoading, deleteUser } = useUserManagementHooks.useDeleteUser();

  const handleDetail = (id: string, userId: string) => {
    const path = location.pathname.replace(/\/$/, '');
    navigate(`${path}/${id}/?userId=${userId}`);
  }

  const handleDelete = async (id: string) => {
    await deleteUser(id);
  }

  const handleChangeStatus = async (id: string) => {
    await toggleUserStatus(id);
  }

  const handleSizeChange = (page: number) => {
    changePage(page);
    search();
  }

  const subscriptionMap = {
    'videoSubscribe': t('agent_dashboard.user_management.new_table.user_subscription_map.video'),
    'blogSubscribe': t('agent_dashboard.user_management.new_table.user_subscription_map.podcast'),
    'momentsSubscribe': t('agent_dashboard.user_management.new_table.user_subscription_map.moments'),
    'graphicSubscribe': t('agent_dashboard.user_management.new_table.user_subscription_map.image'),
    'articleSubscribe': t('agent_dashboard.user_management.new_table.user_subscription_map.article'),
    'aiSubscribe': t('agent_dashboard.user_management.new_table.user_subscription_map.ai'),
    'chatSubscribe': t('agent_dashboard.user_management.new_table.user_subscription_map.ai_chat'),
  }

  const hasSubscribed = useCallback((item: AgentUsersData) => {
    return Object.keys(subscriptionMap).some(key => item[key]);
  }, [subscriptionMap]);

  return (
    <div className='relative flex-1 flex flex-col rounded-lg border overflow-hidden'>
      <div
        className="overflow-auto flex-1"
      >
        <table className="w-full rounded-t-lg relative">
          <thead className='bg-gray-50 sticky top-0 border-b border-[rgba(234,236,240,1)] h-11 z-20'>
            <tr className="bg-gray-50">
              <Th>{t('agent_dashboard.user_management.new_table.user_nickname')}/{t('agent_dashboard.user_management.new_table.user_id')}</Th>
              <Th className='min-w-[7rem]'>{t('agent_dashboard.user_management.new_table.user_email')}</Th>
              <Th>{t('agent_dashboard.user_management.new_table.permission_status')}</Th>
              <Th>{t('agent_dashboard.user_management.new_table.app_status')}</Th>
              <Th>{t('agent_dashboard.user_management.new_table.user_subscription')}</Th>
              <Th className='min-w-[7rem]'>{t('agent_dashboard.user_management.new_table.deadline_for_use')}</Th>
              <Th className='sticky right-0 bg-gray-50 min-w-[7rem] max-w-28'></Th>
            </tr>
          </thead>
          <tbody className='bg-white'>
            {users?.length > 0 && users?.map((item, index) => (
              <tr key={`${item.id}_${index}`} className='border-b h-18 transition-colors duration-200'>
                <Td >
                  <TruncatedText text={item.userName} className='text-[rgba(16,24,40,1)] pb-1' />
                  {item.userGlobalId ? <div className='flex items-center'>
                    <TruncatedText text={item.userGlobalId} className='mr-1' />
                    <CopyClipboard text={item.userGlobalId} />
                  </div> : '-'}
                </Td>
                <Td>
                  <div className='flex items-center'>
                    <TruncatedText text={item.email} className='mr-1' />
                    <CopyClipboard text={item.email} />
                  </div>
                </Td>
                <Td className='min-w-[7rem]'>
                  <span className={cn(
                    "py-0.5 px-2 rounded-lg text-sm font-medium",
                    item.roleStatus === 1 && "text-[rgba(6,118,71,1)] bg-[rgba(236,253,243,1)]",
                    item.roleStatus === 0 && "text-[rgba(52,64,84,1)] bg-[rgba(242,244,247,1)]",
                  )}>{item.roleStatus === 1 ? t('agent_dashboard.user_management.new_table.permission_status_map.enable') : t('agent_dashboard.user_management.new_table.permission_status_map.disable')}</span>
                </Td>
                <Td>
                  <div className='flex items-center '>
                    <div className={cn(
                      "size-2 rounded-full mr-1",
                      item.appStatus === 1 && "bg-[rgba(23,178,106,1)]",
                      item.appStatus === 0 && "bg-[rgba(240,68,56,1)]",
                    )}></div>
                    <span>{item.appStatus === 1 ? t('agent_dashboard.user_management.new_table.app_status_map.verified') : t('agent_dashboard.user_management.new_table.app_status_map.unverified')}</span>
                  </div>
                </Td>
                <Td className='min-w-[26rem]'>
                  {!hasSubscribed(item) && '-'}
                  {hasSubscribed(item) && (
                    <div className='min-w-min flex gap-1.5 flex-shrink-0'>
                      {Object.keys(subscriptionMap)?.filter(key => item[key])?.map(key => (
                        <span className={cn(
                          "py-1 px-2 rounded-lg text-sm font-medium flex-shrink-0",
                          key === 'videoSubscribe' && item[key] && "text-[rgba(23,92,211,1)] bg-[rgba(239,248,255,1)]",
                          key === 'blogSubscribe' && item[key] && "text-[rgba(89,37,220,1)] bg-[rgba(244,243,255,1)]",
                          key === 'momentsSubscribe' && item[key] && "text-[rgba(181,71,8,1)] bg-[rgba(255,250,235,1)]",
                          key === 'graphicSubscribe' && item[key] && "text-[rgba(193,21,116,1)] bg-[rgba(253,242,250,1)]",
                          key === 'articleSubscribe' && item[key] && "text-[rgba(6,118,118,1)] bg-[rgba(238,254,255,1)]",
                          key === 'aiSubscribe' && item[key] && "text-[#026AA2] bg-[#F0F9FF]",
                          key === 'chatSubscribe' && item[key] && "text-[#8841C6] bg-[#F4F0FF]",
                        )}>
                          {subscriptionMap[key]}
                        </span>
                      ))}
                    </div>
                  )}
                </Td>
                <Td className='min-w-[10rem]'>{item.expireAt ? formatDateToYYYYMMDD(item.expireAt) : '-'}</Td>
                <Td className='sticky right-0 bg-white min-w-[7rem] max-w-28 z-[5] group-hover:bg-gray-50 space-x-5 flex items-center py-4 h-full'>
                  <ExpirationVerify>
                    <IconButton
                      className='size-10 rounded-[8px]'
                      icon={<EditIcon className='size-6 text-[rgba(16.24,40,1)]' />}
                      onClick={() => handleDetail(item.id, item.userId)}
                    />
                  </ExpirationVerify>
                  <MoreActionPopover
                    side='left'
                    className='w-40'
                    tiggerIcon={
                      <IconButton
                        className='size-10 rounded-[8px]'
                        icon={<MoreIcon className='size-6 text-[rgba(16.24,40,1)]' />}
                      />
                    }
                  >
                    <ConfirmModal
                      trigger={
                        <ExpirationVerify className='w-full rounded-md'>
                          <MoreActionItem
                            icon={item.roleStatus === 1 ? (
                              <DisableIcon className="w-5 h-5" />
                            ) : <EnableIcon className="w-5 h-5" />
                            }
                            label={item.roleStatus === 1 ? t('agent_dashboard.user_management.table.disable') : t('agent_dashboard.user_management.table.enable')}
                          />
                        </ExpirationVerify>
                      }
                      title={item.roleStatus === 1 ? t('agent_dashboard.user_management.table.disable') : t('agent_dashboard.user_management.table.enable')}
                      description={item.roleStatus === 1 ? t('agent_dashboard.user_management.table.disable_desc') : t('agent_dashboard.user_management.table.enable_desc')}
                      cancel={t('agent_dashboard.confirm_modal.cancel')}
                      confirm={t('agent_dashboard.confirm_modal.confirm')}
                      onConfirm={async () => handleChangeStatus(item.id)}
                      loading={toggleLoading}
                    />
                    {/* <ConfirmModal
                      trigger={(
                        <MoreActionItem
                          icon={<DeleteIcon className="w-5 h-5" />}
                          label={t('agent_dashboard.user_management.table.delete')}
                        />
                      )}
                      title={t('agent_dashboard.confirm_modal.delete_title')}
                      description={t('agent_dashboard.confirm_modal.delete_desc')}
                      cancel={t('agent_dashboard.confirm_modal.cancel')}
                      confirm={t('agent_dashboard.confirm_modal.confirm')}
                      onConfirm={async () => handleDelete(item.id)}
                      loading={deleteLoading}
                    /> */}
                  </MoreActionPopover>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='h-[4.25rem] border-t border-[rgba(234,236,240,1)] px-6 flex items-center'>
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={handleSizeChange}
        />
      </div>
      <LoadingOverlay loading={loading} />
    </div>
  )
}

const Th = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <th className={cn(
    'min-w-[6.25rem] max-w-[12.5rem] pl-6 text-left text-sm font-semibold text-[rgba(16,24,40,1)]',
    className,
  )}>
    {children}
  </th>
)

const Td = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <td className={cn(
    'min-w-[6.25rem] max-w-[12.5rem]  pl-6 text-sm font-medium  text-[rgba(71,84,103,1)]',
    className,
  )}>
    {children}
  </td>
)